import React, { useEffect, useRef } from 'react';

/**
 * Instagram-style pinch-to-zoom image.
 *
 * A two-finger pinch scales the image around the pinch midpoint and follows the
 * fingers' drift; releasing snaps it smoothly back to 1×. Single-finger touches
 * are ignored so the parent's swipe / scroll still works.
 *
 * Uses native, NON-passive touch listeners so it can preventDefault the pinch
 * gesture (React's synthetic touch handlers are passive and can't). Drop-in
 * replacement for an <img>.
 */
export default function PinchZoomImage({ src, alt = '', className, draggable = false, enabled = true, ...rest }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    let pinching = false;
    let startDist = 0;
    let startMidX = 0;
    let startMidY = 0;

    const distOf = (t) => Math.hypot(t[1].clientX - t[0].clientX, t[1].clientY - t[0].clientY);
    const midOf = (t) => [(t[0].clientX + t[1].clientX) / 2, (t[0].clientY + t[1].clientY) / 2];

    const onStart = (e) => {
      if (e.touches.length !== 2) return;
      startDist = distOf(e.touches);
      [startMidX, startMidY] = midOf(e.touches);
      const r = el.getBoundingClientRect();
      // Zoom around where the fingers are, like Instagram.
      el.style.transformOrigin =
        `${((startMidX - r.left) / r.width) * 100}% ${((startMidY - r.top) / r.height) * 100}%`;
      el.style.transition = 'none';
      el.style.zIndex = '60';
      el.style.position = 'relative';
      el.style.willChange = 'transform';
      pinching = true;
    };

    const onMove = (e) => {
      if (!pinching || e.touches.length !== 2) return;
      // Stop the browser/parent from treating this as a scroll or page-zoom.
      e.preventDefault();
      e.stopPropagation();
      const scale = Math.max(1, Math.min(4, distOf(e.touches) / startDist));
      const [mx, my] = midOf(e.touches);
      el.style.transform = `translate(${mx - startMidX}px, ${my - startMidY}px) scale(${scale})`;
    };

    const onEnd = (e) => {
      if (!pinching || e.touches.length >= 2) return;
      pinching = false;
      el.style.transition = 'transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1)';
      el.style.transform = 'translate(0, 0) scale(1)';
      const clear = () => {
        el.style.zIndex = '';
        el.style.position = '';
        el.style.transformOrigin = '';
        el.style.willChange = '';
        el.removeEventListener('transitionend', clear);
      };
      el.addEventListener('transitionend', clear);
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled]);

  return <img ref={ref} src={src} alt={alt} className={className} draggable={draggable} {...rest} />;
}
